import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Row, Column } from '@narvar/nth-grid';
import { BigHeader } from '@narvar/nth-header';
import Divider from '@narvar/nth-divider';
import TextLink from '@narvar/nth-text-link';
import TextInput from '@narvar/nth-text-input';
import Button from '@narvar/nth-button';
import isEmpty from 'lodash/isEmpty';
import {
  togglePrinterlessInfoEmailInput,
  updateEmailInput,
  emailBarcode,
} from '../../actions/printerlessInfo';
import PrinterlessBarcode from '../PrinterlessBarcode';
import NarvarFooter from '../NarvarFooter';
import PrinterlessCustomerSupport from '../PrinterlessCustomerSupport';
import CarrierLogo from '../CarrierLogo';
import patternInputValidation from '../../lib/patternInputValidation';
import { EMAIL_ADDRESS_PATTERN } from '../../constants/validationPatterns';
import DEFAULT_COPY from '../../constants/defaultCopy';
import AdditionalDetails from '../AdditionalDetails';
import { HAPPY_RETURNS, LOCKER_DROPOFF } from '../../constants/returnMethods';
import { StoreLogo } from '../StoreLogo/StoreLogo';
import { formatAddressMultiLine } from '../../lib/addresses';

const DEFAULT_EN_COPY = DEFAULT_COPY.en;

const METHOD_MAPPING = {
  printerless_mail: 'printerless',
  boxless: 'boxless',
  pickup: 'printerless',
  [HAPPY_RETURNS]: HAPPY_RETURNS,
  [LOCKER_DROPOFF]: LOCKER_DROPOFF,
};

const LocationText = styled.p`
  padding: 0;
  margin: 0;
`;

const StoreLogoContainer = styled.div`
  margin-bottom: 1rem;
`;

const mapStateToProps = ({
  retailerSettings: {
    branding: {
      link_color: linkColor,
      link_hover_color: linkHoverColor,
      brand_call_to_action: ctaColor,
      brand_call_to_action_hover: ctaHoverColor,
    } = {},
  } = {},
  language,
  printerlessInfo: {
    apiFailure,
    carrier,
    customerEmail,
    showEmailInput,
    emailInput,
    totalItemQuantity,
    items,
    labelUrl,
    emailSent,
    alternateEmailAddress,
    dropOffLocation,
    orderMethod = 'printerless_mail',
  } = {},
  language: {
    [`returns.${METHOD_MAPPING[orderMethod]}_info_page.title`]: title,
    [`returns.${METHOD_MAPPING[orderMethod]}_${carrier}_info_page.email_confirmation`]:
      carrierSendEmailMessageFormat,
    [`returns.${METHOD_MAPPING[orderMethod]}_info_page.email_confirmation`]:
      genericSendEmailMessageFormat,
    [`returns.${METHOD_MAPPING[orderMethod]}_info_page.email_secondary_link`]: sendEmailLinkText,
    [`returns.${METHOD_MAPPING[orderMethod]}_info_page.email_input_label`]: sendEmailInputLabel,
    [`returns.${METHOD_MAPPING[orderMethod]}_info_page.email_secondary_send_button`]:
      sendEmailButtonText,
    [`returns.${METHOD_MAPPING[orderMethod]}_info_page.email_secondary_confirmation`]:
      emailSentMessageFormat,
    [`returns.${METHOD_MAPPING[orderMethod]}_info_page.location_header`]: locationHeader,
    [`returns.${METHOD_MAPPING[orderMethod]}_info_page.item_quantity_header_format`]:
      itemQuantityHeaderFormat,
    [`returns.${METHOD_MAPPING[orderMethod]}_${carrier}_info_page.print_label_header`]:
      carrierPrintLabelHeader,
    [`returns.${METHOD_MAPPING[orderMethod]}_info_page.print_label_header`]:
      genericPrintLabelHeader,
    [`returns.${METHOD_MAPPING[orderMethod]}_info_page.print_label_subheader`]: printLabelSubheader,
    [`returns.${METHOD_MAPPING[orderMethod]}_info_page.print_label_link`]: printLabelText,
  } = {},
  page: { productType } = {},
  returnsSettings: {
    [productType]: {
      printerlessInfo: { itemPropertiesBlockLayout } = {},
      disableSecondaryEmail = false,
    } = {},
  } = {},
}) => ({
  language,
  apiFailure,
  linkColor,
  linkHoverColor,
  ctaColor,
  ctaHoverColor,
  carrier,
  title,
  sendEmailMessageFormat: carrierSendEmailMessageFormat || genericSendEmailMessageFormat,
  sendEmailLinkText,
  sendEmailInputLabel,
  customerEmail,
  showEmailInput,
  emailInput,
  sendEmailButtonText,
  emailSentMessageFormat,
  emailSent,
  alternateEmailAddress,
  dropOffLocation,
  locationHeader,
  itemQuantityHeaderFormat,
  totalItemQuantity,
  items,
  printLabelHeader: carrierPrintLabelHeader || genericPrintLabelHeader,
  printLabelSubheader,
  printLabelText,
  labelUrl,
  orderMethod,
  languageMethodKey: METHOD_MAPPING[orderMethod],
  disableSecondaryEmail,
  itemPropertiesBlockLayout,
});

const PrinterlessInfoPage = ({
  dispatch,
  language,
  apiFailure,
  linkColor,
  linkHoverColor,
  ctaColor,
  ctaHoverColor,
  carrier,
  title,
  sendEmailMessageFormat,
  sendEmailLinkText,
  sendEmailInputLabel,
  customerEmail,
  showEmailInput,
  emailInput,
  sendEmailButtonText,
  emailSentMessageFormat,
  emailSent,
  alternateEmailAddress,
  dropOffLocation,
  locationHeader,
  itemQuantityHeaderFormat,
  totalItemQuantity,
  items,
  printLabelHeader,
  printLabelSubheader,
  printLabelText,
  labelUrl,
  orderMethod,
  languageMethodKey,
  disableSecondaryEmail,
}) => {
  const isEmailInputValid = patternInputValidation(emailInput, EMAIL_ADDRESS_PATTERN);
  return (
    <div
      className="printerless-info-container"
      style={{
        margin: '0 auto',
        paddingTop: '1rem',
        textAlign: 'center',
        maxWidth: '57.5rem',
      }}
    >
      {apiFailure ? (
        <PrinterlessCustomerSupport />
      ) : (
        <div>
          {title && <BigHeader s={{ margin: '1.5rem auto' }}>{title}</BigHeader>}
          {carrier && orderMethod !== LOCKER_DROPOFF && (
            <div style={{ marginTop: '1rem' }}>
              <CarrierLogo carrier={carrier} defaultLogo={() => null} />
            </div>
          )}
          <PrinterlessBarcode />
          <Divider />
          <p>{sendEmailMessageFormat.replace('$email', customerEmail)}</p>
          {!disableSecondaryEmail && (
            <React.Fragment>
              <TextLink
                s={{
                  color: linkColor,
                  hoverColor: linkHoverColor,
                  textDecoration: 'underline',
                }}
                onClick={() => dispatch(togglePrinterlessInfoEmailInput())}
              >
                {sendEmailLinkText}
              </TextLink>
              <div>
                {showEmailInput && (
                  <div
                    style={{
                      width: '60%',
                      margin: '0 auto',
                    }}
                  >
                    <Row>
                      <Column xs-columns={12} xs-style="margin-bottom: 0.5rem;" md-columns={10}>
                        <TextInput
                          className="fs-exclude"
                          label={sendEmailInputLabel}
                          defaultValue={emailInput}
                          onChange={({ target: { value = '' } } = {}) =>
                            dispatch(updateEmailInput(value))
                          }
                        />
                      </Column>
                      <Column xs-columns={12} md-columns={2}>
                        <Button
                          className="narvar-btn-primary"
                          disabled={!isEmailInputValid || !emailInput}
                          big
                          s={{
                            height: '3.125rem',
                            backgroundColor: ctaColor,
                            hover: {
                              backgroundColor: ctaHoverColor,
                            },
                          }}
                          onClick={() => dispatch(emailBarcode())}
                        >
                          {sendEmailButtonText}
                        </Button>
                      </Column>
                    </Row>
                  </div>
                )}
                {emailSent && !showEmailInput && (
                  <p>{emailSentMessageFormat.replace('$email', alternateEmailAddress)}</p>
                )}
              </div>
            </React.Fragment>
          )}

          <Divider />
          {!isEmpty(dropOffLocation) && (
            <React.Fragment>
              <div>
                <React.Fragment>
                  {language[
                    `returns.${languageMethodKey}_info_page.location.${dropOffLocation.location_name}`
                  ] ? (
                    <p>
                      <strong>
                        {locationHeader.replace(
                          '$name',
                          language[
                            `returns.${languageMethodKey}_info_page.location.${dropOffLocation.location_name}`
                          ],
                        )}
                      </strong>
                    </p>
                  ) : null}
                </React.Fragment>
                {dropOffLocation.logo && (
                  <StoreLogoContainer>
                    <StoreLogo logo={dropOffLocation.logo} alt={dropOffLocation.location_name} />
                  </StoreLogoContainer>
                )}
                <address>
                  {formatAddressMultiLine({
                    countryCode: dropOffLocation.country_code,
                    address: {
                      company: dropOffLocation.display_label,
                      address1: dropOffLocation.street_1,
                      address2: dropOffLocation.street_2,
                      city: dropOffLocation.city,
                      region: dropOffLocation.state,
                      postalCode: dropOffLocation.zip,
                    },
                  }).map((text) => (
                    <LocationText>{text}</LocationText>
                  ))}
                </address>

                <AdditionalDetails
                  items={
                    (dropOffLocation.additional_details &&
                      dropOffLocation.additional_details.business_info) ||
                    []
                  }
                  stacked
                />
              </div>
              <Divider />
            </React.Fragment>
          )}
          <p>
            <strong>{itemQuantityHeaderFormat.replace('$itemQuantity', totalItemQuantity)}</strong>
          </p>
          {items.map(({ name, sku, propertiesString, propertiesBlock }) => (
            <div
              key={`${name}${sku}`}
              style={{
                marginBottom: '1.5rem',
              }}
              data-test={name}
            >
              <p dangerouslySetInnerHTML={{ __html: name }} />
              {propertiesBlock ? (
                propertiesBlock.map((content) => <p>{content}</p>)
              ) : (
                <p>{propertiesString}</p>
              )}
            </div>
          ))}
          {labelUrl ? (
            <React.Fragment>
              <Divider />
              <div className="reprint-label-container">
                <p className="reprint-label-header">
                  <strong>{printLabelHeader}</strong>
                </p>
                <p className="reprint-label-subheader">{printLabelSubheader}</p>
                <TextLink
                  className="reprint-label-link"
                  href={labelUrl}
                  target="_blank"
                  s={{
                    color: linkColor,
                    hoverColor: linkHoverColor,
                    textDecoration: 'underline',
                  }}
                >
                  {printLabelText}
                </TextLink>
              </div>
            </React.Fragment>
          ) : null}
        </div>
      )}
      <div
        style={{
          marginTop: '1.25rem',
        }}
      >
        <NarvarFooter />
      </div>
    </div>
  );
};

PrinterlessInfoPage.defaultProps = {
  linkColor: '',
  linkHoverColor: '',
  carrier: null,
  customerEmail: '',
  dropOffLocation: null,
  totalItemQuantity: 0,
  items: [],
  labelUrl: null,
  languageMethodKey: 'printerless',
  title: undefined,
  sendEmailMessageFormat: DEFAULT_EN_COPY['returns.printerless_info_page.email_confirmation'],
  sendEmailLinkText: DEFAULT_EN_COPY['returns.printerless_info_page.email_secondary_link'],
  sendEmailInputLabel: DEFAULT_EN_COPY['returns.printerless_info_page.email_input_label'],
  sendEmailButtonText: DEFAULT_EN_COPY['returns.printerless_info_page.email_secondary_send_button'],
  emailSentMessageFormat:
    DEFAULT_EN_COPY['returns.printerless_info_page.email_secondary_confirmation'],
  locationHeader: DEFAULT_EN_COPY['returns.printerless_info_page.location_header'],
  itemQuantityHeaderFormat:
    DEFAULT_EN_COPY['returns.printerless_info_page.item_quantity_header_format'],
  printLabelHeader: DEFAULT_EN_COPY['returns.printerless_info_page.print_label_header'],
  printLabelSubheader: DEFAULT_EN_COPY['returns.printerless_info_page.print_label_subheader'],
  printLabelText: DEFAULT_EN_COPY['returns.printerless_info_page.print_label_link'],
  orderMethod: null,
  disableSecondaryEmail: false,
  itemPropertiesBlockLayout: false,
};

PrinterlessInfoPage.propTypes = {
  dispatch: PropTypes.func.isRequired,
  language: PropTypes.object.isRequired,
  apiFailure: PropTypes.bool.isRequired,
  linkColor: PropTypes.string,
  linkHoverColor: PropTypes.string,
  ctaColor: PropTypes.string.isRequired,
  ctaHoverColor: PropTypes.string.isRequired,
  carrier: PropTypes.string,
  title: PropTypes.string,
  sendEmailMessageFormat: PropTypes.string,
  sendEmailLinkText: PropTypes.string,
  customerEmail: PropTypes.string,
  showEmailInput: PropTypes.bool.isRequired,
  sendEmailInputLabel: PropTypes.string,
  emailInput: PropTypes.string.isRequired,
  sendEmailButtonText: PropTypes.string,
  emailSentMessageFormat: PropTypes.string,
  emailSent: PropTypes.bool.isRequired,
  alternateEmailAddress: PropTypes.string.isRequired,
  dropOffLocation: PropTypes.shape({
    location_id: PropTypes.number,
    location_name: PropTypes.string,
    display_label: PropTypes.string,
    phone: PropTypes.string,
    lat: PropTypes.number,
    lng: PropTypes.number,
    street_1: PropTypes.string,
    street_2: PropTypes.string,
    state: PropTypes.string,
    city: PropTypes.string,
    zip: PropTypes.string,
    zip_2: PropTypes.string,
    country: PropTypes.string,
    country_code: PropTypes.string,
  }),
  locationHeader: PropTypes.string,
  itemQuantityHeaderFormat: PropTypes.string,
  totalItemQuantity: PropTypes.number,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      properties: PropTypes.string,
    }),
  ),
  printLabelHeader: PropTypes.string,
  printLabelSubheader: PropTypes.string,
  printLabelText: PropTypes.string,
  labelUrl: PropTypes.string,
  orderMethod: PropTypes.string,
  languageMethodKey: PropTypes.string,
  disableSecondaryEmail: PropTypes.bool,
  itemPropertiesBlockLayout: PropTypes.bool,
};

export { PrinterlessInfoPage, mapStateToProps };
export default connect(mapStateToProps)(PrinterlessInfoPage);
